import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Code2, Star, ChevronRight, ExternalLink } from 'lucide-react';
import GitmindImage from '../project_images/Gitmind.png';
import HackMate from '../project_images/HackMate.png';
import StoryCraft from '../project_images/StoryCraft.jpeg';
import Numina from '../project_images/numina.png';
import RealSt from '../project_images/real-estate.png';
import chatPdf from '../project_images/chatpdf.png';
import webbuilder from '../project_images/web-builder.png';
import Attention from '../project_images/Attention.png';
import RL from '../project_images/RL.png';
import fractal from '../project_images/fractal.png';
const projectData = [
  {
    title: 'GitMind',
    description: 'AI-powered documentation generator that automatically creates comprehensive documentation for your GitHub repositories using advanced language models.',
    tech: ['React', 'TailwindCSS', 'LLM', 'flask', 'MongoDB'],
    image: GitmindImage,
    github: 'https://github.com/arjun988/GITMIND',
    category: ['AI', 'GenAI', 'Full Stack'],
    featured: true,
  },
  {
    title: 'HackMate',
    description: 'Interactive platform that generates personalized programming challenges and provides real-time feedback using AI. Helps developers improve their coding skills.',
    tech: ['React', 'Flask', 'Python', 'SQL', 'TailwindCSS', 'LLM'],
    image: HackMate,
    github: 'https://github.com/arjun988/HackMate',
    category: ['AI', 'GenAI', 'Full Stack'],
    featured: true,
  },
  {
    title: 'NuminaJS',
    description: 'A javascript library for Data Science',
    tech: ['javascript'],
    image: Numina,
    link: 'https://www.npmjs.com/package/numinajs',
    category: ['package'],
    featured: true,
  },
  {
    title: 'Fractal Regularization',
    description: 'Developed and designed a new regularization algorithm that is inspired from fractals',
    tech: ['Tensorflow', 'numpy','pytorch'],
    image: fractal,
    link: 'https://pypi.org/project/fractal-regularization/',
    github: 'https://drive.google.com/file/d/1qhlPx5Vof8dIBa1hvy2fftzJo9LgHIEG/view?usp=sharing',
    category: ['AI','Deep Learning','package'],
    featured: true,
  },
  {
    title: 'Attention is all you need',
    description: 'Implementated the research paper Attention is all you need from scratch',
    tech: ['Tensorflow', 'numpy'],
    image: Attention,
    github: 'https://github.com/arjun988/Attention_is-_all_you_need',
    category: ['AI', 'Deep Learning'],
    featured: true,
  },
  {
    title: 'Real Estate Chatbot(Dubai)',
    description: 'A chatbot for finding best locations of real estate properties',
    tech: ['LLM', 'RAG', 'Streamlit', 'python'],
    image: RealSt,
    link: 'https://arjun988-dubai-realestate-chatbot-realestate-qsomhq.streamlit.app/',
    category: ['RAG', 'AI', 'Chatbot'],
    featured: true,
  },
  {
    title: 'Website Builder',
    description: 'Build a simple website using a simple prompt and keep on updating it by directly interacting with website',
    tech: ['LLM', 'RAG', 'Streamlit', 'python'],
    image: webbuilder,
    github: 'https://github.com/arjun988/website-builder',
    category: ['RAG', 'AI', 'Chatbot'],
    featured: true,
  },
  {
    title: 'ChatPdf',
    description: 'Upload the PDF and chat with it to gain insights',
    tech: ['LLM', 'RAG', 'Streamlit', 'python'],
    image: chatPdf,
    link: 'https://chatpdf-arjun.streamlit.app/',
    category: ['RAG', 'AI', 'Chatbot'],
    featured: true,
  },
  {
    title: 'StoryCraft',
    description: 'AI-driven storytelling platform that helps writers generate creative ideas, develop plot lines, and get feedback on their writing through advanced NLP.',
    tech: ['React Native', 'Django', 'LLM', 'PostgreSQL'],
    image: StoryCraft,
    github: 'https://github.com/TechManTejas/StoryCraft_Client',
    category: ['AI', 'GenAI', 'Full Stack'],
    featured: true,
  },
  {
    title: 'Reinforcement Learning : Ping Pong Game ',
    description: 'Implementated the entire algorithm and game learning platform from scratch',
    tech: ['Tensorflow', 'numpy', 'matplotlib'],
    image: RL,
    github: 'https://github.com/arjun988/PingPong_RL',
    category: ['AI','Reinforcement Learning', 'Deep Learning'],
    featured: true,
  },
];

const Projects = () => {
  const [filter, setFilter] = useState('All');
  const categories = ['All', ...new Set(projectData.flatMap((p) => p.category))];
  const { scrollY } = useScroll();
  const opacitySection = useTransform(scrollY, [100, 200], [0, 1]);
  const ySection = useTransform(scrollY, [100, 300], [100, 0]);
  
  // Particle animation setup
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    // Initialize particles
    const particleCount = 30;
    const newParticles = [];
    
    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        size: Math.random() * 2 + 0.5,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.3 + 0.05
      });
    }
    
    setParticles(newParticles);
    
    // Particle animation loop
    const interval = setInterval(() => {
      setParticles(prevParticles => 
        prevParticles.map(p => ({
          ...p,
          x: (p.x + p.speedX + window.innerWidth) % window.innerWidth,
          y: (p.y + p.speedY + window.innerHeight) % window.innerHeight
        }))
      );
    }, 50);
    
    return () => clearInterval(interval);
  }, []);

  const filteredProjects = projectData.filter((project) =>
    filter === 'All' ? true : project.category.includes(filter)
  );

  return (
    <section id="projects" className="py-20 bg-black relative overflow-hidden min-h-screen flex flex-col justify-center">
      {/* Neural network background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:20px_20px] opacity-20" />
      
      {/* Animated particles */}
      {particles.map((particle, index) => (
        <motion.div
          key={index}
          className="absolute rounded-full bg-white pointer-events-none"
          style={{
            x: particle.x,
            y: particle.y,
            width: particle.size,
            height: particle.size,
            opacity: particle.opacity,
          }}
          animate={{
            opacity: [particle.opacity, particle.opacity * 1.5, particle.opacity],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}

      <motion.div 
        className="max-w-6xl mx-auto px-4 relative z-10 w-full"
        style={{ opacity: opacitySection, y: ySection }}
      >
        <motion.div 
          className="text-center mb-16 space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div className="relative inline-block">
            <motion.div 
              className="absolute -inset-8 bg-gradient-to-r from-white/5 to-gray-500/5 rounded-full blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <h2 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
              Featured Projects
            </h2>
          </motion.div>
          <motion.p 
            className="text-gray-400 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Explore my latest work and personal projects
          </motion.p>
        </motion.div>

        {/* Filter buttons */}
        <motion.div 
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          {categories.map((category, index) => (
            <motion.button
              key={category}
              onClick={() => setFilter(category)}
              className={`
                px-6 py-2 rounded-full transition-all duration-300 transform
                ${filter === category
                  ? 'bg-white/10 text-white scale-105 shadow-lg shadow-white/10'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:scale-105'}
                hover:shadow-lg hover:shadow-white/10
              `}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>

        {/* Project cards grid */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          {filteredProjects.map((project, index) => (
            <ProjectCard key={project.title} project={project} index={index} />
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

const ProjectCard = ({ project, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        group relative rounded-xl overflow-hidden 
        bg-white/5 backdrop-blur-sm
        hover:bg-white/10
        hover:shadow-xl hover:shadow-white/10
      `}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        delay: 0.2 + index * 0.1,
        duration: 0.6,
        ease: "easeOut"
      }}
      whileHover={{ 
        y: -8, 
        transition: { duration: 0.3 } 
      }}
    >
      {/* Featured badge */}
      {project.featured && (
        <motion.div 
          className="absolute top-4 right-4 z-20 flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-white text-sm font-medium"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
        >
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.7, 1, 0.7]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Star className="w-4 h-4" />
          </motion.div>
          <span>Featured</span>
        </motion.div>
      )}

      {/* Image container with overlay */}
      <div className="relative overflow-hidden h-48">
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"
          initial={{ opacity: 0.5 }}
          animate={{ opacity: isHovered ? 0.7 : 0.5 }}
          transition={{ duration: 0.3 }}
        />
        <motion.img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover"
          initial={{ scale: 1 }}
          animate={{ scale: isHovered ? 1.1 : 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
      </div>

      <div className="relative p-6 space-y-4">
        <h3 className="text-2xl font-bold">
          <motion.span 
            className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent inline-block"
            animate={{ 
              backgroundPosition: isHovered ? ['0% 0%', '100% 0%'] : '0% 0%' 
            }}
            transition={{ 
              duration: 1.5, 
              ease: "easeInOut",
              repeat: isHovered ? Infinity : 0,
              repeatType: "reverse"
            }}
            style={{ 
              backgroundSize: '200% 100%'
            }}
          >
            {project.title}
          </motion.span>
        </h3>

        <p className="text-gray-400 leading-relaxed">{project.description}</p>

        <motion.div 
          className="flex flex-wrap gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {project.tech.map((tech, i) => (
            <motion.span
              key={i}
              className="px-3 py-1 text-sm bg-white/10 text-gray-300 rounded-full"
              whileHover={{ 
                scale: 1.05,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "#ffffff",
                boxShadow: "0 0 10px rgba(255, 255, 255, 0.1)"
              }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1, duration: 0.3 }}
            >
              {tech}
            </motion.span>
          ))}
        </motion.div>

        <motion.div 
          className="flex gap-6 pt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          {project.github && (
            <motion.a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-400 group/link"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Code2 className="w-5 h-5 transition-colors duration-300 group-hover/link:text-white" />
              <span className="text-sm transition-colors duration-300 group-hover/link:text-white">Code</span>
            </motion.a>
          )}
          {project.link && (
            <motion.a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-400 group/link"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ExternalLink className="w-5 h-5 transition-colors duration-300 group-hover/link:text-white" />
              <span className="text-sm transition-colors duration-300 group-hover/link:text-white">Live Demo</span>
              <motion.div
                initial={{ x: 0 }}
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronRight className="w-4 h-4" />
              </motion.div>
            </motion.a>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Projects;